"use client";
import { datadogRum } from "@datadog/browser-rum";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  type FleetGraphqlApiSelection,
  resolveFleetGraphqlApi,
} from "./fleet-graphql-api";

const legacyUrl = process.env.NEXT_PUBLIC_GRAPHQL_API ?? "";
const usdpUrl = process.env.NEXT_PUBLIC_GRAPHQL_API_USDP;

const FleetGraphqlApiContext = createContext<FleetGraphqlApiSelection>({
  url: legacyUrl,
  service: "legacy",
});

export default function FleetGraphqlApiProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { enableUsdpFleetGraphqlApi } = useFlags();
  // Pinned for the page lifetime: the asset-index worker takes its GraphQL URL
  // once at init, so a mid-session flag flip must not split one session's
  // requests across the two deployments.
  const [selection] = useState(() =>
    resolveFleetGraphqlApi({
      enableUsdpFleetGraphqlApi,
      legacyUrl,
      usdpUrl,
    }),
  );

  useEffect(() => {
    if (selection.fallbackReason) {
      console.warn(
        "[fleet-graphql-api] EnableUsdpFleetGraphqlApi is ON but " +
          "NEXT_PUBLIC_GRAPHQL_API_USDP is not set; staying on the legacy " +
          "fleet-graphql-api deployment",
      );
    }
    // Tags every RUM event with the deployment this session uses, so errors
    // and latency can be compared between the two.
    if (datadogRum.getInitConfiguration() === undefined) return;
    datadogRum.setGlobalContextProperty("fleetGraphqlApi", selection.service);
  }, [selection]);

  return (
    <FleetGraphqlApiContext.Provider value={selection}>
      {children}
    </FleetGraphqlApiContext.Provider>
  );
}

export const useFleetGraphqlApiUrl = () =>
  useContext(FleetGraphqlApiContext).url;
